import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { EnvelopeClosedIcon, ArrowRightIcon, ArrowLeftIcon } from '@radix-ui/react-icons';
import { authService } from '../../services/authService';
import { Input, Button } from '../../components';
import { AuthLayout } from './AuthLayout';
import { validateEmail } from '../../utils/validation';
import { useEmailValidation } from '../../hooks/useEmailValidation';
import { formatApiError, getFieldErrorsFromApi } from '../../utils/errorHandler';
import { ApiError } from '../../types/api';
import './Auth.scss';

interface LocationState {
  email?: string;
}

export const ResetPasswordRequest: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const prefilledEmail = (location.state as LocationState | null)?.email ?? '';
  const [email, setEmail] = useState(prefilledEmail);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const {
    error: emailError,
    onBlur: onEmailBlur,
    onChange: onEmailChange,
    validate: validateEmailField,
    setError: setEmailError,
  } = useEmailValidation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const emailResult = validateEmailField(email);
    if (!emailResult.valid) {
      return;
    }

    setIsLoading(true);
    try {
      await authService.forgotPassword({ email: emailResult.trimmed });
      navigate('/reset-password/sent', { state: { email: emailResult.trimmed } });
    } catch (err) {
      const apiError = err as ApiError;
      const fieldErrors = getFieldErrorsFromApi(apiError);
      if (fieldErrors.email) {
        setEmailError(fieldErrors.email);
      } else {
        setError(formatApiError(apiError) || 'Failed to send reset link. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const canSubmit = validateEmail(email).valid && !emailError;

  const getSubmitDisabledReason = (): string | undefined => {
    if (isLoading) return 'Sending reset link...';
    if (emailError) return emailError;
    const emailResult = validateEmail(email);
    if (!emailResult.valid) return emailResult.error;
    return undefined;
  };

  return (
    <AuthLayout>
      <div className="auth-card">
        <form onSubmit={handleSubmit} className="auth-form" noValidate>
          <h1 className="auth-title">Reset Password</h1>
          <p className="auth-subtitle">
            Enter the email address associated with your account and we will send you a link to reset your password.
          </p>

          <div className="auth-field">
            <label htmlFor="reset-email" className="auth-field-label">
              <EnvelopeClosedIcon className="auth-field-icon" aria-hidden="true" />
              Email Address
            </label>
            <Input
              id="reset-email"
              type="email"
              placeholder="name@company.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError(null);
                onEmailChange(e.target.value);
              }}
              onBlur={() => onEmailBlur(email)}
              error={emailError}
              autoComplete="email"
            />
          </div>

          {error && (
            <div className="auth-error" role="alert">
              {error}
            </div>
          )}

          <Button
            type="submit"
            fullWidth
            className="auth-submit-button"
            disabled={isLoading || !canSubmit}
            disabledReason={getSubmitDisabledReason()}
          >
            {isLoading ? 'Sending...' : 'Send Reset Link'}
            {!isLoading && <ArrowRightIcon aria-hidden="true" />}
          </Button>

          <Link to="/login" className="auth-back-link">
            <ArrowLeftIcon aria-hidden="true" />
            Back to Login
          </Link>
        </form>
      </div>
    </AuthLayout>
  );
};
